/**
 * Get the user's system timezone using Intl API
 * Falls back to America/New_York if detection fails
 */
export function getSystemTimezone(): string {
  try {
    // Modern browsers support Intl.DateTimeFormat().resolvedOptions()
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Validate it's a valid IANA timezone
    if (timezone && isValidIANATimezone(timezone)) {
      return timezone;
    }
    
    // Fallback to America/New_York if detection fails
    console.warn('Could not detect valid timezone, using default');
    return 'America/New_York';
  } catch (error) {
    console.error('Error detecting timezone:', error);
    return 'America/New_York';
  }
}

/**
 * Validate if a string is a valid IANA timezone
 */
export function isValidIANATimezone(timezone: string): boolean {
  try {
    // Try to create a DateTimeFormat with the timezone
    // If it's invalid, it will throw an error
    Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get human-readable timezone name
 * Example: "Eastern Standard Time" or "Pacific Daylight Time"
 */
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    
    return timeZonePart?.value || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Get UTC offset for a timezone
 * Example: "-05:00" for Eastern Time
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset'
    });
    
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    
    return offsetPart?.value || '';
  } catch {
    return '';
  }
}
